import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { theme } from '../../../theme';
import DashboardLayout from '../../../components/dashboard/DashboardLayout';
import { Card, CardBody } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Table } from '../../../components/ui/Table';
import { Modal } from '../../../components/ui/Modal';
import { useAuth } from '../../../contexts/AuthContext';
import toast from 'react-hot-toast';
import { FiPlus, FiEye, FiDownload, FiSend, FiFileText, FiEdit, FiCheck, FiX, FiRefreshCw } from 'react-icons/fi';
import {
  fetchInvoiceManagement,
  addInvoiceManagement,
  getInvoiceManagementById,
  updateInvoiceManagement,
  approveInvoice,
  rejectInvoice,
  recheckInvoice,
  InvoiceManagement,
} from '../service/invoiceManagementService';
import { createAccountReceivable } from '../service/accountReceivableService';
import { Timestamp } from 'firebase/firestore';
import { fetchBankClients, BankClientData } from '../../manage_clients/bank_clients/service/bankClientService';
import { fetchFactoryClients, FactoryClientData } from '../../manage_clients/factory_clients/service/factoryClientService';
import { fetchIndividualClients, IndividualClientData } from '../../manage_clients/individual_clients/service/individualClientService';
import { Select } from '../../../components/ui/Select';

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing[6]};
`;

const PageTitle = styled.h1`
  font-size: ${theme.typography.fontSize['2xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.secondary[900]};
  margin: 0;
`;

const FormGroup = styled.div`
  margin-bottom: ${theme.spacing[4]};
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${theme.spacing[4]};
  align-items: start;

  @media (max-width: ${theme.breakpoints.md}) {
    grid-template-columns: 1fr;
  }
`;

const StyledTextarea = styled.textarea`
  width: 100%;
  padding: ${theme.spacing[3]};
  border: 1px solid ${theme.colors.light.border.medium};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.fontSize.base};
  font-family: ${theme.typography.fontFamily.primary};
  background-color: ${theme.colors.white};
  color: ${theme.colors.light.text.primary};
  resize: vertical;
  min-height: 80px;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}20;
  }

  &::placeholder {
    color: ${theme.colors.light.text.muted};
  }
`;

const StatusBadge = styled.span<{ status: string }>`
  display: inline-block;
  padding: ${theme.spacing[1]} ${theme.spacing[3]};
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.typography.fontSize.xs};
  font-weight: ${theme.typography.fontWeight.medium};
  background-color: ${({ status }) => {
    switch (status) {
      case 'paid':
        return `${theme.colors.accent.success}20`;
      case 'submitted':
        return `${theme.colors.primary[500]}20`;
      case 'sent':
        return `${theme.colors.primary[500]}20`;
      case 'recheck':
        return `${theme.colors.primary[400]}20`;
      case 'overdue':
        return `${theme.colors.accent.danger}20`;
      case 'rejected':
        return `${theme.colors.accent.danger}20`;
      case 'cancelled':
        return `${theme.colors.light.background.tertiary}`;
      default:
        return `${theme.colors.accent.warning}20`;
    }
  }};
  color: ${({ status }) => {
    switch (status) {
      case 'paid':
        return theme.colors.accent.success;
      case 'submitted':
        return theme.colors.primary[600];
      case 'sent':
        return theme.colors.primary[600];
      case 'recheck':
        return theme.colors.primary[500];
      case 'overdue':
        return theme.colors.accent.danger;
      case 'rejected':
        return theme.colors.accent.danger;
      case 'cancelled':
        return theme.colors.light.text.secondary;
      default:
        return theme.colors.accent.warning;
    }
  }};
`;

const ActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing[2]};
  border: none;
  background-color: transparent;
  color: ${theme.colors.light.text.secondary};
  cursor: pointer;
  border-radius: ${theme.borderRadius.md};
  transition: all 0.2s ease;
  margin-right: ${theme.spacing[2]};

  &:hover {
    background-color: ${theme.colors.light.background.tertiary};
    color: ${theme.colors.primary[600]};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const AmountCell = styled.div<{ amount: number }>`
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${({ amount }) => (amount >= 0 ? theme.colors.accent.success : theme.colors.accent.danger)};
  text-align: right;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${theme.spacing[12]} ${theme.spacing[6]};
  color: ${theme.colors.light.text.secondary};
`;

const EmptyStateText = styled.p`
  font-size: ${theme.typography.fontSize.lg};
  margin: 0;
`;

const InvoiceViewContainer = styled.div`
  padding: ${theme.spacing[4]};
`;

const InvoiceHeader = styled.div`
  text-align: center;
  margin-bottom: ${theme.spacing[8]};
  border-bottom: 2px solid ${theme.colors.light.border.medium};
  padding-bottom: ${theme.spacing[4]};
`;

const InvoiceTitle = styled.h2`
  font-size: ${theme.typography.fontSize['2xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.light.text.primary};
  margin: 0 0 ${theme.spacing[2]} 0;
`;

const InvoiceNumber = styled.div`
  font-size: ${theme.typography.fontSize.base};
  color: ${theme.colors.light.text.secondary};
  margin-bottom: ${theme.spacing[4]};
`;

const InvoiceDetails = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${theme.spacing[6]};
  margin-bottom: ${theme.spacing[6]};
`;

const DetailSection = styled.div`
  h3 {
    font-size: ${theme.typography.fontSize.base};
    font-weight: ${theme.typography.fontWeight.semibold};
    color: ${theme.colors.light.text.primary};
    margin-bottom: ${theme.spacing[2]};
  }
  p {
    font-size: ${theme.typography.fontSize.sm};
    color: ${theme.colors.light.text.secondary};
    margin: ${theme.spacing[1]} 0;
  }
`;

const InvoiceTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-bottom: ${theme.spacing[6]};
  
  th, td {
    padding: ${theme.spacing[3]};
    text-align: left;
    border-bottom: 1px solid ${theme.colors.light.border.light};
  }
  
  th {
    font-weight: ${theme.typography.fontWeight.semibold};
    color: ${theme.colors.light.text.primary};
    background-color: ${theme.colors.light.background.secondary};
  }
  
  td {
    color: ${theme.colors.light.text.secondary};
  }
  
  .text-right {
    text-align: right;
  }
  
  .total-row {
    font-weight: ${theme.typography.fontWeight.bold};
    font-size: ${theme.typography.fontSize.lg};
    background-color: ${theme.colors.light.background.secondary};
  }
`;

const InvoiceFooter = styled.div`
  margin-top: ${theme.spacing[8]};
  padding-top: ${theme.spacing[4]};
  border-top: 2px solid ${theme.colors.light.border.medium};
  text-align: center;
  color: ${theme.colors.light.text.muted};
  font-size: ${theme.typography.fontSize.sm};
`;

interface ClientOption {
  id: string;
  name: string;
  type: 'Individual' | 'Factory' | 'Bank';
  email?: string;
}

const InvoiceManagementPage: React.FC = () => {
  const { user, companyData, staffData } = useAuth();
  const companyId = companyData?.id || '';

  // Check if user is a company owner/admin (logged in from companies, not staff)
  const userEmail = user?.email?.toLowerCase();
  const isCompanyOwner = !!companyData && (
    !staffData || // No staffData = pure company owner
    (userEmail && (
      companyData.email?.toLowerCase() === userEmail || 
      companyData.emails?.some((email: string) => email.toLowerCase() === userEmail)
    )) // Email matches company = company owner
  );

  // Check permissions for invoice management
  const isStaffUser = !!staffData && !isCompanyOwner;
  const canEditInvoiceManagement = isCompanyOwner || (isStaffUser && staffData?.permissions?.invoiceManagement === 'edit');

  const [invoices, setInvoices] = useState<InvoiceManagement[]>([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [isRecheckModalOpen, setIsRecheckModalOpen] = useState(false);
  const [isReasonModalOpen, setIsReasonModalOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<InvoiceManagement | null>(null);
  const [editingInvoice, setEditingInvoice] = useState<InvoiceManagement | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [recheckReason, setRecheckReason] = useState('');
  const [clients, setClients] = useState<ClientOption[]>([]);
  const [loadingClients, setLoadingClients] = useState(false);
  
  const [formData, setFormData] = useState({
    clientId: '',
    clientName: '',
    clientType: 'Bank' as 'Individual' | 'Factory' | 'Bank',
    clientEmail: '',
    date: new Date().toISOString().split('T')[0],
    dueDate: '',
    serviceDescription: '',
    totalGuards: '',
    guardDeploymentAmount: '',
    totalAmount: '',
    notes: '',
  });

  useEffect(() => {
    if (companyId) {
      loadInvoices();
      loadClients();
    }
  }, [companyId]);

  const loadClients = async () => {
    if (!companyId) return;
    setLoadingClients(true);
    try {
      const [bankClients, factoryClients, individualClients] = await Promise.all([
        fetchBankClients(companyId).catch(() => []),
        fetchFactoryClients(companyId).catch(() => []),
        fetchIndividualClients(companyId).catch(() => []),
      ]);

      const allClients: ClientOption[] = [
        ...bankClients.map((client) => ({
          id: client.id,
          name: `${client.bankName || client.name}${client.branchCode ? ` - ${client.branchCode}` : ''}`,
          type: 'Bank' as const,
          email: client.mobile ? undefined : undefined, // Bank clients might not have email
        })),
        ...factoryClients.map((client) => ({
          id: client.id,
          name: client.name,
          type: 'Factory' as const,
        })),
        ...individualClients.map((client) => ({
          id: client.id,
          name: client.name,
          type: 'Individual' as const,
        })),
      ];

      setClients(allClients);
    } catch (error: any) {
      console.error('Error loading clients:', error);
      toast.error('Failed to load clients');
    } finally {
      setLoadingClients(false);
    }
  };

  const loadInvoices = async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const data = await fetchInvoiceManagement(companyId);
      setInvoices(data);
    } catch (error: any) {
      console.error('Error loading invoices:', error);
      toast.error(error.message || 'Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  const handleClientChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedClientId = e.target.value;
    const client = clients.find((c) => c.id === selectedClientId);
    
    if (client) {
      setFormData({
        ...formData,
        clientId: client.id,
        clientName: client.name,
        clientType: client.type,
        clientEmail: client.email || '',
      });
    }
  };

  const calculateTotalAmount = () => {
    const guards = parseFloat(formData.totalGuards) || 0;
    const guardAmount = parseFloat(formData.guardDeploymentAmount) || 0;
    const calculatedTotal = guards * guardAmount;
    
    if (calculatedTotal > 0) {
      setFormData((prev) => ({
        ...prev,
        totalAmount: calculatedTotal.toFixed(2),
      }));
    }
  };

  useEffect(() => {
    calculateTotalAmount();
  }, [formData.totalGuards, formData.guardDeploymentAmount]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!companyId) {
      toast.error('Company data not found');
      return;
    }

    if (!formData.clientId || !formData.serviceDescription || !formData.totalAmount) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      await addInvoiceManagement(companyId, {
        clientId: formData.clientId,
        clientName: formData.clientName,
        clientType: formData.clientType,
        clientEmail: formData.clientEmail || undefined,
        date: new Date(formData.date),
        dueDate: formData.dueDate ? new Date(formData.dueDate) : undefined,
        serviceDescription: formData.serviceDescription,
        totalGuards: parseInt(formData.totalGuards) || 0,
        guardDeploymentAmount: parseFloat(formData.guardDeploymentAmount) || 0,
        totalAmount: parseFloat(formData.totalAmount) || 0,
        notes: formData.notes || undefined,
      });

      toast.success('Invoice created successfully');
      setIsModalOpen(false);
      setFormData({
        clientId: '',
        clientName: '',
        clientType: 'Bank',
        clientEmail: '',
        date: new Date().toISOString().split('T')[0],
        dueDate: '',
        serviceDescription: '',
        totalGuards: '',
        guardDeploymentAmount: '',
        totalAmount: '',
        notes: '',
      });
      await loadInvoices();
    } catch (error: any) {
      console.error('Error creating invoice:', error);
      toast.error(error.message || 'Failed to create invoice');
    } finally {
      setLoading(false);
    }
  };

  const handleViewInvoice = async (invoice: InvoiceManagement) => {
    try {
      const fullInvoice = await getInvoiceManagementById(companyId, invoice.id);
      if (fullInvoice) {
        setSelectedInvoice(fullInvoice);
        setIsViewModalOpen(true);
      }
    } catch (error: any) {
      console.error('Error loading invoice:', error);
      toast.error('Failed to load invoice details');
    }
  };

  const handleDownloadPDF = async (invoice: InvoiceManagement) => {
    try {
      // Create a printable invoice HTML
      const invoiceHTML = generateInvoiceHTML(invoice);
      
      // Create a new window for printing
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(invoiceHTML);
        printWindow.document.close();
        printWindow.onload = () => {
          printWindow.print();
        };
      }
    } catch (error: any) {
      console.error('Error generating PDF:', error);
      toast.error('Failed to generate PDF');
    }
  };

  const handleEmailInvoice = async (invoice: InvoiceManagement) => {
    if (!invoice.clientEmail) {
      toast.error('Client email not available');
      return;
    }

    try {
      const subject = encodeURIComponent(`Invoice ${invoice.invoiceNumber} - ${companyData?.companyName || 'Guard Security'}`);
      const body = encodeURIComponent(
        `Dear ${invoice.clientName},\n\n` +
        `Please find attached invoice ${invoice.invoiceNumber} for security services.\n\n` +
        `Total Amount: PKR ${invoice.totalAmount.toLocaleString()}\n` +
        `Due Date: ${formatDate(invoice.dueDate)}\n\n` +
        `Thank you for your business.\n\n` +
        `${companyData?.companyName || 'Guard Security'}`
      );
      
      window.location.href = `mailto:${invoice.clientEmail}?subject=${subject}&body=${body}`;
    } catch (error: any) {
      console.error('Error opening email:', error);
      toast.error('Failed to open email client');
    }
  };

  const handleEditInvoice = (invoice: InvoiceManagement) => {
    if (invoice.status !== 'pending' && invoice.status !== 'recheck') {
      toast.error('Only pending or recheck invoices can be edited');
      return;
    }
    setEditingInvoice(invoice);
    setFormData({
      clientId: invoice.clientId,
      clientName: invoice.clientName,
      clientType: invoice.clientType,
      clientEmail: invoice.clientEmail || '',
      date: invoice.date instanceof Date 
        ? invoice.date.toISOString().split('T')[0]
        : (invoice.date as any).toDate 
          ? (invoice.date as any).toDate().toISOString().split('T')[0]
          : new Date(invoice.date as any).toISOString().split('T')[0],
      dueDate: invoice.dueDate 
        ? (invoice.dueDate instanceof Date
          ? invoice.dueDate.toISOString().split('T')[0]
          : (invoice.dueDate as any).toDate
            ? (invoice.dueDate as any).toDate().toISOString().split('T')[0]
            : new Date(invoice.dueDate as any).toISOString().split('T')[0])
        : '',
      serviceDescription: invoice.serviceDescription,
      totalGuards: invoice.totalGuards.toString(),
      guardDeploymentAmount: invoice.guardDeploymentAmount.toString(),
      totalAmount: invoice.totalAmount.toString(),
      notes: invoice.notes || '',
    });
    setIsEditModalOpen(true);
  };

  const handleUpdateInvoice = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!companyId || !editingInvoice) {
      toast.error('Company data or invoice not found');
      return;
    }

    if (!formData.clientId || !formData.serviceDescription || !formData.totalAmount) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const updateData: any = {
        clientId: formData.clientId,
        clientName: formData.clientName,
        clientType: formData.clientType,
        date: new Date(formData.date),
        serviceDescription: formData.serviceDescription,
        totalGuards: parseInt(formData.totalGuards) || 0,
        guardDeploymentAmount: parseFloat(formData.guardDeploymentAmount) || 0,
        totalAmount: parseFloat(formData.totalAmount) || 0,
      };

      // Only add optional fields if they have values
      if (formData.clientEmail && formData.clientEmail.trim()) {
        updateData.clientEmail = formData.clientEmail.trim();
      }
      if (formData.dueDate) {
        updateData.dueDate = new Date(formData.dueDate);
      }
      if (formData.notes && formData.notes.trim()) {
        updateData.notes = formData.notes.trim();
      }

      await updateInvoiceManagement(companyId, editingInvoice.id, updateData);

      toast.success('Invoice updated successfully');
      setIsEditModalOpen(false);
      setEditingInvoice(null);
      await loadInvoices();
    } catch (error: any) {
      console.error('Error updating invoice:', error);
      toast.error(error.message || 'Failed to update invoice');
    } finally {
      setLoading(false);
    }
  };

  const handleApproveInvoice = async (invoice: InvoiceManagement) => {
    if (invoice.status !== 'pending' && invoice.status !== 'recheck') {
      toast.error('Only pending or recheck invoices can be approved');
      return;
    }

    if (!companyId) {
      toast.error('Company data not found');
      return;
    }

    setLoading(true);
    try {
      await approveInvoice(companyId, invoice.id);
      await createAccountReceivable(companyId, invoice);
      toast.success('Invoice approved and added to Account Receivable');
      await loadInvoices();
    } catch (error: any) {
      console.error('Error approving invoice:', error);
      toast.error(error.message || 'Failed to approve invoice');
    } finally {
      setLoading(false);
    }
  };

  const handleRejectInvoiceClick = (invoice: InvoiceManagement) => {
    if (invoice.status !== 'pending' && invoice.status !== 'recheck') {
      toast.error('Only pending or recheck invoices can be rejected');
      return;
    }
    setSelectedInvoice(invoice);
    setRejectReason('');
    setIsRejectModalOpen(true);
  };

  const handleRejectInvoice = async () => {
    if (!companyId || !selectedInvoice || !rejectReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }

    setLoading(true);
    try {
      await rejectInvoice(companyId, selectedInvoice.id, rejectReason.trim());
      toast.success('Invoice rejected');
      setIsRejectModalOpen(false);
      setSelectedInvoice(null);
      setRejectReason('');
      await loadInvoices();
    } catch (error: any) {
      console.error('Error rejecting invoice:', error);
      toast.error(error.message || 'Failed to reject invoice');
    } finally {
      setLoading(false);
    }
  };

  const handleRecheckInvoiceClick = (invoice: InvoiceManagement) => {
    if (invoice.status !== 'rejected' && invoice.status !== 'pending') {
      toast.error('Only rejected or pending invoices can be rechecked');
      return;
    }
    setSelectedInvoice(invoice);
    setRecheckReason('');
    setIsRecheckModalOpen(true);
  };

  const handleRecheckInvoice = async () => {
    if (!companyId || !selectedInvoice || !recheckReason.trim()) {
      toast.error('Please provide a recheck reason');
      return;
    }

    setLoading(true);
    try {
      await recheckInvoice(companyId, selectedInvoice.id, recheckReason.trim());
      toast.success('Invoice status changed to recheck');
      setIsRecheckModalOpen(false);
      setSelectedInvoice(null);
      setRecheckReason('');
      await loadInvoices();
    } catch (error: any) {
      console.error('Error rechecking invoice:', error);
      toast.error(error.message || 'Failed to recheck invoice');
    } finally {
      setLoading(false);
    }
  };

  const generateInvoiceHTML = (invoice: InvoiceManagement): string => {
    const invoiceDate = formatDate(invoice.date);
    const dueDate = invoice.dueDate ? formatDate(invoice.dueDate) : null;
    
    return `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Invoice ${invoice.invoiceNumber}</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              margin: 40px;
              color: #333;
            }
            .header {
              text-align: center;
              margin-bottom: 40px;
              border-bottom: 2px solid #333;
              padding-bottom: 20px;
            }
            .company-info {
              margin-bottom: 30px;
            }
            .invoice-details {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 30px;
              margin-bottom: 30px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-bottom: 30px;
            }
            th, td {
              padding: 12px;
              text-align: left;
              border-bottom: 1px solid #ddd;
            }
            th {
              background-color: #f5f5f5;
              font-weight: bold;
            }
            .total-row {
              font-weight: bold;
              font-size: 18px;
              background-color: #f5f5f5;
            }
            .text-right {
              text-align: right;
            }
            .footer {
              margin-top: 40px;
              padding-top: 20px;
              border-top: 2px solid #333;
              text-align: center;
              color: #666;
              font-size: 12px;
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>INVOICE</h1>
            <p>Invoice Number: ${invoice.invoiceNumber}</p>
          </div>
          
          <div class="company-info">
            <h3>${companyData?.companyName || companyData?.name || 'Guard Security Company'}</h3>
            ${companyData?.address ? `<p>${companyData.address}</p>` : ''}
            ${companyData?.email ? `<p>Email: ${companyData.email}</p>` : ''}
            ${companyData?.phone ? `<p>Phone: ${companyData.phone}</p>` : ''}
          </div>
          
          <div class="invoice-details">
            <div>
              <h3>Bill To:</h3>
              <p><strong>${invoice.clientName}</strong></p>
              <p>Type: ${invoice.clientType}</p>
              ${invoice.clientEmail ? `<p>Email: ${invoice.clientEmail}</p>` : ''}
            </div>
            <div>
              <p><strong>Invoice Date:</strong> ${invoiceDate}</p>
              ${dueDate ? `<p><strong>Due Date:</strong> ${dueDate}</p>` : ''}
              <p><strong>Status:</strong> ${invoice.status.toUpperCase()}</p>
            </div>
          </div>
          
          <table>
            <thead>
              <tr>
                <th>Description</th>
                <th class="text-right">Quantity</th>
                <th class="text-right">Unit Price</th>
                <th class="text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>${invoice.serviceDescription}</td>
                <td class="text-right">${invoice.totalGuards} Guards</td>
                <td class="text-right">PKR ${invoice.guardDeploymentAmount.toLocaleString()}</td>
                <td class="text-right">PKR ${(invoice.totalGuards * invoice.guardDeploymentAmount).toLocaleString()}</td>
              </tr>
              <tr class="total-row">
                <td colspan="3" class="text-right"><strong>Total Amount:</strong></td>
                <td class="text-right"><strong>PKR ${invoice.totalAmount.toLocaleString()}</strong></td>
              </tr>
            </tbody>
          </table>
          
          ${invoice.notes ? `<div><strong>Notes:</strong><p>${invoice.notes}</p></div>` : ''}
          
          <div class="footer">
            <p>Thank you for your business!</p>
            <p>${companyData?.companyName || companyData?.name || 'Guard Security Company'}</p>
          </div>
        </body>
      </html>
    `;
  };

  const formatDate = (date: Date | Timestamp | undefined): string => {
    if (!date) return '—';
    let dateObj: Date;
    if (date instanceof Date) {
      dateObj = date;
    } else if ((date as any).toDate && typeof (date as any).toDate === 'function') {
      dateObj = (date as any).toDate();
    } else if ((date as any).seconds) {
      dateObj = new Date((date as any).seconds * 1000);
    } else {
      dateObj = new Date(date as any);
    }
    return dateObj.toLocaleDateString();
  };

  return (
    <DashboardLayout>
      <PageHeader>
        <PageTitle>Invoice Management</PageTitle>
        {canEditInvoiceManagement && (
          <Button onClick={() => setIsModalOpen(true)}>
            <FiPlus size={18} style={{ marginRight: theme.spacing[2] }} />
            Create Invoice
          </Button>
        )}
      </PageHeader>

      <Card>
        <CardBody>
          {loading && invoices.length === 0 ? (
            <EmptyState>
              <EmptyStateText>Loading invoices...</EmptyStateText>
            </EmptyState>
          ) : invoices.length === 0 ? (
            <EmptyState>
              <EmptyStateText>No invoices yet. Create your first invoice to get started.</EmptyStateText>
            </EmptyState>
          ) : (
            <Table>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>Invoice #</Table.HeaderCell>
                  <Table.HeaderCell>Date</Table.HeaderCell>
                  <Table.HeaderCell>Client Info</Table.HeaderCell>
                  <Table.HeaderCell>Description</Table.HeaderCell>
                  <Table.HeaderCell style={{ textAlign: 'right' }}>TotalGuards</Table.HeaderCell>
                  <Table.HeaderCell style={{ textAlign: 'right' }}>Amount</Table.HeaderCell>
                  <Table.HeaderCell>Status</Table.HeaderCell>
                  <Table.HeaderCell>Due Date</Table.HeaderCell>
                  <Table.HeaderCell>Actions</Table.HeaderCell>
                  {canEditInvoiceManagement && <Table.HeaderCell>Approval</Table.HeaderCell>}
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {invoices.map((invoice) => (
                  <Table.Row key={invoice.id}>
                    <Table.Cell>
                      <strong>{invoice.invoiceNumber}</strong>
                    </Table.Cell>
                    <Table.Cell>{formatDate(invoice.date)}</Table.Cell>
                    <Table.Cell>
                      <div>
                        <div style={{ fontWeight: theme.typography.fontWeight.medium, marginBottom: theme.spacing[1] }}>
                          {invoice.clientName}
                        </div>
                        <div style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.light.text.secondary }}>
                          {invoice.clientType}
                        </div>
                      </div>
                    </Table.Cell>
                    <Table.Cell>{invoice.serviceDescription}</Table.Cell>
                    <Table.Cell style={{ textAlign: 'right' }}>{invoice.totalGuards}</Table.Cell>
                    <Table.Cell style={{ textAlign: 'right' }}>
                      <AmountCell amount={invoice.totalAmount}>
                        PKR {invoice.totalAmount.toLocaleString()}
                      </AmountCell>
                    </Table.Cell>
                    <Table.Cell>
                      <div>
                        <StatusBadge status={invoice.status}>
                          {invoice.status === 'submitted' ? 'Submitted' : 
                           invoice.status === 'recheck' ? 'Re-check' :
                           invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                        </StatusBadge>
                        {invoice.status === 'recheck' && invoice.recheckReason && (
                          <div
                            onClick={() => {
                              setSelectedInvoice(invoice);
                              setIsReasonModalOpen(true);
                            }}
                            style={{
                              marginTop: theme.spacing[1],
                              fontSize: theme.typography.fontSize.xs,
                              color: theme.colors.light.text.secondary,
                              cursor: 'pointer',
                              textDecoration: 'underline',
                              maxWidth: '200px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                            title={invoice.recheckReason}
                          >
                            {invoice.recheckReason.length > 30 
                              ? `${invoice.recheckReason.substring(0, 30)}...` 
                              : invoice.recheckReason}
                          </div>
                        )}
                      </div>
                    </Table.Cell>
                    <Table.Cell>{formatDate(invoice.dueDate)}</Table.Cell>
                    <Table.Cell>
                      <ActionButton
                        onClick={() => handleViewInvoice(invoice)}
                        title="View Invoice"
                      >
                        <FiEye size={18} />
                      </ActionButton>
                      <ActionButton
                        onClick={() => handleDownloadPDF(invoice)}
                        title="Download PDF"
                      >
                        <FiDownload size={18} />
                      </ActionButton>
                      {canEditInvoiceManagement && (invoice.status === 'pending' || invoice.status === 'recheck') && (
                        <ActionButton
                          onClick={() => handleEditInvoice(invoice)}
                          title="Edit Invoice"
                        >
                          <FiEdit size={18} />
                        </ActionButton>
                      )}
                      {invoice.clientEmail && (
                        <ActionButton
                          onClick={() => handleEmailInvoice(invoice)}
                          title="Email Invoice"
                        >
                          <FiSend size={18} />
                        </ActionButton>
                      )}
                    </Table.Cell>
                    {canEditInvoiceManagement && (
                      <Table.Cell>
                        {invoice.status === 'pending' && (
                          <div style={{ display: 'flex', gap: theme.spacing[2], alignItems: 'center' }}>
                            <ActionButton
                              onClick={() => handleApproveInvoice(invoice)}
                              title="Approve Invoice"
                              style={{ color: theme.colors.accent.success }}
                            >
                              <FiCheck size={18} />
                            </ActionButton>
                            <ActionButton
                              onClick={() => handleRejectInvoiceClick(invoice)}
                              title="Reject Invoice"
                              style={{ color: theme.colors.accent.danger }}
                            >
                              <FiX size={18} />
                            </ActionButton>
                            <ActionButton
                              onClick={() => handleRecheckInvoiceClick(invoice)}
                              title="Recheck Invoice"
                              style={{ color: theme.colors.primary[600] }}
                            >
                              <FiRefreshCw size={18} />
                            </ActionButton>
                          </div>
                        )}
                        {invoice.status === 'rejected' && (
                          <ActionButton
                            onClick={() => handleRecheckInvoiceClick(invoice)}
                            title="Recheck Invoice"
                            style={{ color: theme.colors.primary[600] }}
                          >
                            <FiRefreshCw size={18} />
                          </ActionButton>
                        )}
                        {invoice.status !== 'pending' && invoice.status !== 'rejected' && (
                          <span style={{ color: theme.colors.light.text.muted }}>—</span>
                        )}
                      </Table.Cell>
                    )}
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          )}
        </CardBody>
      </Card>

      {/* Create Invoice Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Create Invoice"
        footer={
          <>
            <Button variant="secondary" onClick={() => setIsModalOpen(false)} disabled={loading}>
              Cancel
            </Button>
            <Button onClick={() => { const form = document.getElementById('invoice-form') as HTMLFormElement; if (form) form.requestSubmit(); }} loading={loading} type="submit">
              Create Invoice
            </Button>
          </>
        }
      >
        <form id="invoice-form" onSubmit={handleSubmit}>
          <FormGroup>
            <Select
              name="clientId"
              label="Client *"
              value={formData.clientId}
              onChange={handleClientChange}
              required
              disabled={loadingClients}
              dark
              fullWidth
            >
              <option value="">Select Client</option>
              <optgroup label="Bank Clients">
                {clients.filter(c => c.type === 'Bank').map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.name}
                  </option>
                ))}
              </optgroup>
              <optgroup label="Factory Clients">
                {clients.filter(c => c.type === 'Factory').map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.name}
                  </option>
                ))}
              </optgroup>
              <optgroup label="Individual Clients">
                {clients.filter(c => c.type === 'Individual').map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.name}
                  </option>
                ))}
              </optgroup>
            </Select>
          </FormGroup>

          <FormRow>
            <FormGroup>
              <Input
                label="Invoice Date *"
                name="date"
                type="date"
                value={formData.date}
                onChange={handleInputChange}
                required
                fullWidth
                dark
              />
            </FormGroup>
            <FormGroup>
              <Input
                label="Due Date"
                name="dueDate"
                type="date"
                value={formData.dueDate}
                onChange={handleInputChange}
                fullWidth
                dark
              />
            </FormGroup>
          </FormRow>

          <FormGroup>
            <label style={{ 
              display: 'block', 
              fontSize: theme.typography.fontSize.sm, 
              fontWeight: theme.typography.fontWeight.medium,
              color: theme.colors.light.text.primary,
              marginBottom: theme.spacing[1]
            }}>
              Service Description *
            </label>
            <StyledTextarea
              name="serviceDescription"
              value={formData.serviceDescription}
              onChange={handleInputChange}
              required
              rows={3}
              placeholder="e.g., Security services for bank branch - Guard deployment"
            />
          </FormGroup>

          <FormRow>
            <FormGroup>
              <Input
                label="Total Guards"
                name="totalGuards"
                type="text"
                value={formData.totalGuards}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9]/g, '');
                  setFormData((prev) => ({ ...prev, totalGuards: value }));
                }}
                required={false}
                fullWidth
                dark
                placeholder="Number of guards deployed"
              />
            </FormGroup>
            <FormGroup>
              <Input
                label="Guard Deployment Amount (per guard)"
                name="guardDeploymentAmount"
                type="text"
                value={formData.guardDeploymentAmount}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9.]/g, '');
                  setFormData((prev) => ({ ...prev, guardDeploymentAmount: value }));
                }}
                required={false}
                fullWidth
                dark
                placeholder="Amount per guard"
              />
            </FormGroup>
          </FormRow>

          <FormGroup>
            <Input
              label="Total Amount *"
              name="totalAmount"
              type="text"
              value={formData.totalAmount}
              onChange={(e) => {
                const value = e.target.value.replace(/[^0-9.]/g, '');
                setFormData((prev) => ({ ...prev, totalAmount: value }));
              }}
              required
              fullWidth
              dark
              placeholder="Total invoice amount"
            />
            <small style={{ 
              color: theme.colors.light.text.muted, 
              fontSize: theme.typography.fontSize.xs,
              marginTop: theme.spacing[1],
              display: 'block'
            }}>
              Calculated automatically: {formData.totalGuards || 0} guards × PKR {formData.guardDeploymentAmount || 0} = PKR {((parseFloat(formData.totalGuards) || 0) * (parseFloat(formData.guardDeploymentAmount) || 0)).toLocaleString()}
            </small>
          </FormGroup>

          <FormGroup>
            <label style={{ 
              display: 'block', 
              fontSize: theme.typography.fontSize.sm, 
              fontWeight: theme.typography.fontWeight.medium,
              color: theme.colors.light.text.primary,
              marginBottom: theme.spacing[1]
            }}>
              Notes
            </label>
            <StyledTextarea
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              rows={3}
              placeholder="Additional notes or terms"
            />
          </FormGroup>
        </form>
      </Modal>

      {/* View Invoice Modal */}
      <Modal
        isOpen={isViewModalOpen}
        onClose={() => {
          setIsViewModalOpen(false);
          setSelectedInvoice(null);
        }}
        title={`Invoice ${selectedInvoice?.invoiceNumber || ''}`}
        footer={
          <>
            <Button variant="secondary" onClick={() => setIsViewModalOpen(false)}>
              Close
            </Button>
            {selectedInvoice && (
              <>
                <div style={{ display: 'inline-block', marginLeft: theme.spacing[2] }}>
                  <Button
                    variant="secondary"
                    onClick={() => handleDownloadPDF(selectedInvoice)}
                  >
                    <FiDownload size={18} style={{ marginRight: theme.spacing[2] }} />
                    Download PDF
                  </Button>
                </div>
                {selectedInvoice.clientEmail && (
                  <div style={{ display: 'inline-block', marginLeft: theme.spacing[2] }}>
                    <Button
                      variant="secondary"
                      onClick={() => handleEmailInvoice(selectedInvoice)}
                    >
                      <FiSend size={18} style={{ marginRight: theme.spacing[2] }} />
                      Email
                    </Button>
                  </div>
                )}
              </>
            )}
          </>
        }
      >
        {selectedInvoice && (
          <InvoiceViewContainer>
            <InvoiceHeader>
              <InvoiceTitle>INVOICE</InvoiceTitle>
              <InvoiceNumber>Invoice Number: {selectedInvoice.invoiceNumber}</InvoiceNumber>
            </InvoiceHeader>

            <InvoiceDetails>
              <DetailSection>
                <h3>Company Information</h3>
                <p><strong>{companyData?.companyName || companyData?.name || 'Guard Security Company'}</strong></p>
                {companyData?.address && <p>{companyData.address}</p>}
                {companyData?.email && <p>Email: {companyData.email}</p>}
                {companyData?.phone && <p>Phone: {companyData.phone}</p>}
              </DetailSection>
              <DetailSection>
                <h3>Bill To</h3>
                <p><strong>{selectedInvoice.clientName}</strong></p>
                <p>Type: {selectedInvoice.clientType}</p>
                {selectedInvoice.clientEmail && <p>Email: {selectedInvoice.clientEmail}</p>}
              </DetailSection>
            </InvoiceDetails>

            <InvoiceDetails>
              <DetailSection>
                <h3>Invoice Details</h3>
                <p><strong>Invoice Date:</strong> {formatDate(selectedInvoice.date)}</p>
                {selectedInvoice.dueDate && (
                  <p><strong>Due Date:</strong> {formatDate(selectedInvoice.dueDate)}</p>
                )}
                <p><strong>Status:</strong> 
                  <StatusBadge status={selectedInvoice.status} style={{ marginLeft: theme.spacing[2] }}>
                    {selectedInvoice.status.charAt(0).toUpperCase() + selectedInvoice.status.slice(1)}
                  </StatusBadge>
                </p>
              </DetailSection>
              <DetailSection>
                <h3>Service Information</h3>
                <p><strong>Service Description:</strong></p>
                <p>{selectedInvoice.serviceDescription}</p>
              </DetailSection>
            </InvoiceDetails>

            <InvoiceTable>
              <thead>
                <tr>
                  <th>Description</th>
                  <th className="text-right">Quantity</th>
                  <th className="text-right">Unit Price</th>
                  <th className="text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{selectedInvoice.serviceDescription}</td>
                  <td className="text-right">{selectedInvoice.totalGuards} Guards</td>
                  <td className="text-right">PKR {selectedInvoice.guardDeploymentAmount.toLocaleString()}</td>
                  <td className="text-right">PKR {(selectedInvoice.totalGuards * selectedInvoice.guardDeploymentAmount).toLocaleString()}</td>
                </tr>
                <tr className="total-row">
                  <td colSpan={3} className="text-right"><strong>Total Amount:</strong></td>
                  <td className="text-right"><strong>PKR {selectedInvoice.totalAmount.toLocaleString()}</strong></td>
                </tr>
              </tbody>
            </InvoiceTable>

            {selectedInvoice.notes && (
              <DetailSection>
                <h3>Notes</h3>
                <p>{selectedInvoice.notes}</p>
              </DetailSection>
            )}

            <InvoiceFooter>
              <p>Thank you for your business!</p>
              <p>{companyData?.companyName || companyData?.name || 'Guard Security Company'}</p>
            </InvoiceFooter>
          </InvoiceViewContainer>
        )}
      </Modal>

      {/* Edit Invoice Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setEditingInvoice(null);
        }}
        title="Edit Invoice"
        footer={
          <>
            <Button variant="secondary" onClick={() => {
              setIsEditModalOpen(false);
              setEditingInvoice(null);
            }} disabled={loading}>
              Cancel
            </Button>
            <Button onClick={() => { const form = document.getElementById('edit-invoice-form') as HTMLFormElement; if (form) form.requestSubmit(); }} loading={loading} type="submit">
              Save Changes
            </Button>
          </>
        }
      >
        <form id="edit-invoice-form" onSubmit={handleUpdateInvoice}>
          <FormGroup>
            <Select
              name="clientId"
              label="Client *"
              value={formData.clientId}
              onChange={handleClientChange}
              required
              disabled={loadingClients}
              dark
              fullWidth
            >
              <option value="">Select Client</option>
              <optgroup label="Bank Clients">
                {clients.filter(c => c.type === 'Bank').map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.name}
                  </option>
                ))}
              </optgroup>
              <optgroup label="Factory Clients">
                {clients.filter(c => c.type === 'Factory').map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.name}
                  </option>
                ))}
              </optgroup>
              <optgroup label="Individual Clients">
                {clients.filter(c => c.type === 'Individual').map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.name}
                  </option>
                ))}
              </optgroup>
            </Select>
          </FormGroup>

          <FormRow>
            <FormGroup>
              <Input
                label="Invoice Date *"
                name="date"
                type="date"
                value={formData.date}
                onChange={handleInputChange}
                required
                fullWidth
                dark
              />
            </FormGroup>
            <FormGroup>
              <Input
                label="Due Date"
                name="dueDate"
                type="date"
                value={formData.dueDate}
                onChange={handleInputChange}
                fullWidth
                dark
              />
            </FormGroup>
          </FormRow>

          <FormGroup>
            <label style={{ 
              display: 'block', 
              fontSize: theme.typography.fontSize.sm, 
              fontWeight: theme.typography.fontWeight.medium,
              color: theme.colors.light.text.primary,
              marginBottom: theme.spacing[1]
            }}>
              Service Description *
            </label>
            <StyledTextarea
              name="serviceDescription"
              value={formData.serviceDescription}
              onChange={handleInputChange}
              required
              rows={3}
              placeholder="e.g., Security services for bank branch - Guard deployment"
            />
          </FormGroup>

          <FormRow>
            <FormGroup>
              <Input
                label="Total Guards"
                name="totalGuards"
                type="text"
                value={formData.totalGuards}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9]/g, '');
                  setFormData((prev) => ({ ...prev, totalGuards: value }));
                }}
                required={false}
                fullWidth
                dark
                placeholder="Number of guards deployed"
              />
            </FormGroup>
            <FormGroup>
              <Input
                label="Guard Deployment Amount (per guard)"
                name="guardDeploymentAmount"
                type="text"
                value={formData.guardDeploymentAmount}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9.]/g, '');
                  setFormData((prev) => ({ ...prev, guardDeploymentAmount: value }));
                }}
                required={false}
                fullWidth
                dark
                placeholder="Amount per guard"
              />
            </FormGroup>
          </FormRow>

          <FormGroup>
            <Input
              label="Total Amount *"
              name="totalAmount"
              type="text"
              value={formData.totalAmount}
              onChange={(e) => {
                const value = e.target.value.replace(/[^0-9.]/g, '');
                setFormData((prev) => ({ ...prev, totalAmount: value }));
              }}
              required
              fullWidth
              dark
              placeholder="Total invoice amount"
            />
            <small style={{ 
              color: theme.colors.light.text.muted, 
              fontSize: theme.typography.fontSize.xs,
              marginTop: theme.spacing[1],
              display: 'block'
            }}>
              Calculated automatically: {formData.totalGuards || 0} guards × PKR {formData.guardDeploymentAmount || 0} = PKR {((parseFloat(formData.totalGuards) || 0) * (parseFloat(formData.guardDeploymentAmount) || 0)).toLocaleString()}
            </small>
          </FormGroup>

          <FormGroup>
            <label style={{ 
              display: 'block', 
              fontSize: theme.typography.fontSize.sm, 
              fontWeight: theme.typography.fontWeight.medium,
              color: theme.colors.light.text.primary,
              marginBottom: theme.spacing[1]
            }}>
              Notes
            </label>
            <StyledTextarea
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              rows={3}
              placeholder="Additional notes or terms"
            />
          </FormGroup>
        </form>
      </Modal>

      {/* Reject Invoice Modal */}
      <Modal
        isOpen={isRejectModalOpen}
        onClose={() => {
          setIsRejectModalOpen(false);
          setSelectedInvoice(null);
          setRejectReason('');
        }}
        title="Reject Invoice"
        footer={
          <>
            <Button variant="secondary" onClick={() => {
              setIsRejectModalOpen(false);
              setSelectedInvoice(null);
              setRejectReason('');
            }} disabled={loading}>
              Cancel
            </Button>
            <Button onClick={handleRejectInvoice} loading={loading} variant="danger">
              Reject Invoice
            </Button>
          </>
        }
      >
        <FormGroup>
          <label style={{ 
            display: 'block', 
            fontSize: theme.typography.fontSize.sm, 
            fontWeight: theme.typography.fontWeight.medium,
            color: theme.colors.light.text.primary,
            marginBottom: theme.spacing[1]
          }}>
            Rejection Reason *
          </label>
          <StyledTextarea
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            rows={4}
            placeholder="Please provide a reason for rejecting this invoice..."
            required
          />
        </FormGroup>
        {selectedInvoice && (
          <div style={{ 
            marginTop: theme.spacing[4],
            padding: theme.spacing[3],
            backgroundColor: theme.colors.light.background.secondary,
            borderRadius: theme.borderRadius.md
          }}>
            <p style={{ margin: 0, fontSize: theme.typography.fontSize.sm, color: theme.colors.light.text.secondary }}>
              <strong>Invoice:</strong> {selectedInvoice.invoiceNumber}<br />
              <strong>Client:</strong> {selectedInvoice.clientName}<br />
              <strong>Amount:</strong> PKR {selectedInvoice.totalAmount.toLocaleString()}
            </p>
          </div>
        )}
      </Modal>

      {/* Recheck Invoice Modal */}
      <Modal
        isOpen={isRecheckModalOpen}
        onClose={() => {
          setIsRecheckModalOpen(false);
          setSelectedInvoice(null);
          setRecheckReason('');
        }}
        title="Recheck Invoice"
        footer={
          <>
            <Button variant="secondary" onClick={() => {
              setIsRecheckModalOpen(false);
              setSelectedInvoice(null);
              setRecheckReason('');
            }} disabled={loading}>
              Cancel
            </Button>
            <Button onClick={handleRecheckInvoice} loading={loading}>
              Recheck Invoice
            </Button>
          </>
        }
      >
        <FormGroup>
          <label style={{ 
            display: 'block', 
            fontSize: theme.typography.fontSize.sm, 
            fontWeight: theme.typography.fontWeight.medium,
            color: theme.colors.light.text.primary,
            marginBottom: theme.spacing[1]
          }}>
            Recheck Reason *
          </label>
          <StyledTextarea
            value={recheckReason}
            onChange={(e) => setRecheckReason(e.target.value)}
            rows={4}
            placeholder="Please provide a reason for rechecking this invoice..."
            required
          />
        </FormGroup>
        {selectedInvoice && (
          <div style={{ 
            marginTop: theme.spacing[4],
            padding: theme.spacing[3],
            backgroundColor: theme.colors.light.background.secondary,
            borderRadius: theme.borderRadius.md
          }}>
            <p style={{ margin: 0, fontSize: theme.typography.fontSize.sm, color: theme.colors.light.text.secondary }}>
              <strong>Invoice:</strong> {selectedInvoice.invoiceNumber}<br />
              <strong>Client:</strong> {selectedInvoice.clientName}<br />
              <strong>Amount:</strong> PKR {selectedInvoice.totalAmount.toLocaleString()}
            </p>
          </div>
        )}
      </Modal>

      {/* View Reason Modal */}
      <Modal
        isOpen={isReasonModalOpen}
        onClose={() => {
          setIsReasonModalOpen(false);
          setSelectedInvoice(null);
        }}
        title="Recheck Reason"
        footer={
          <Button variant="secondary" onClick={() => {
            setIsReasonModalOpen(false);
            setSelectedInvoice(null);
          }}>
            Close
          </Button>
        }
      >
        {selectedInvoice && selectedInvoice.recheckReason && (
          <div>
            <p style={{ 
              fontSize: theme.typography.fontSize.base,
              color: theme.colors.light.text.primary,
              lineHeight: '1.6',
              margin: 0
            }}>
              {selectedInvoice.recheckReason}
            </p>
            {selectedInvoice.recheckedAt && (
              <p style={{ 
                marginTop: theme.spacing[4],
                fontSize: theme.typography.fontSize.sm,
                color: theme.colors.light.text.secondary
              }}>
                Rechecked on: {formatDate(selectedInvoice.recheckedAt)}
              </p>
            )}
          </div>
        )}
      </Modal>
    </DashboardLayout>
  );
};

export default InvoiceManagementPage;

